import {MdbsCore} from "@/api/tsclient";
import {deepCopy} from "@/auxillary";
import {v4 as uuidv4} from 'uuid';
import {translate} from "@/auxillary/translation";
import {resolveConstraintExpression} from "@/auxillary/constraint.expression.resolver";
import DataFieldDefinitionDto = MdbsCore.DataFieldDefinitionDto;
import DataFieldDto = MdbsCore.DataFieldDto;
import RuleDefinitionDto = MdbsCore.RuleDefinitionDto;
import ITemporaryDataFieldDto = MdbsCore.ITemporaryDataFieldDto;
import {isBranchingFieldType} from "@/auxillary/datafield.cache.helpers";
import {
    DEFINITIONTYPE_EDITABILITY,
    DEFINITIONTYPE_HINT,
    DEFINITIONTYPE_REDIRECT,
    DEFINITIONTYPE_REQUIRED_FIELD,
    DEFINITIONTYPE_VISIBILITY
} from "@/auxillary/rules";
import AuditReasonOfChangeDto = MdbsCore.AuditReasonOfChangeDto;

export default class DataFieldCache {
    private readonly _definition: Readonly<DataFieldDefinitionDto>;
    private readonly _originalField: Readonly<DataFieldDto> | undefined;
    private readonly _isDeleted: boolean;
    private readonly _temporaryId: string = "";
    private _hintRules?: Readonly<Array<RuleDefinitionDto>>;
    private _visibilityRules?: Readonly<Array<RuleDefinitionDto>>;
    private _editabilityRules?: Readonly<Array<RuleDefinitionDto>>;
    private readonly _redirectRule?: Readonly<RuleDefinitionDto> = undefined;
    private _reasonForChange?: AuditReasonOfChangeDto;

    constructor(definition: Readonly<MdbsCore.DataFieldDefinitionDto>, originalField?: Readonly<MdbsCore.DataFieldDto>, temporaryField?: ITemporaryDataFieldDto) {
        this._definition = definition;
        if (originalField === undefined) {
            this._originalField = undefined;
            this._order = definition.order ?? 0;
            this._temporaryId = uuidv4().toString();
            if (temporaryField && temporaryField?.value) {
                this._values = deepCopy(temporaryField?.value);
                this._tags = deepCopy(temporaryField?.tags);
            }
            this._redirectRule = definition.rules?.find(r =>
                r.definitionType == DEFINITIONTYPE_REDIRECT &&
                !r.isDeleted);
            this._isDeleted = definition.isDeleted ?? false;

            return;
        }

        this._reasonForChange = new MdbsCore.AuditReasonOfChangeDto();
        this._originalField = originalField;
        this._isVisible = originalField.isVisible ?? true;
        this._isLockedManually = originalField.lockedManually ?? false;
        this._values = deepCopy(originalField.values)
        this._tags = deepCopy(originalField.tags)
        if (isBranchingFieldType(definition.definitionType)){
            this._order = originalField.order ?? 0;
        } else {
            this._order = definition.order ?? 0;
        }
        this._isDeleted = originalField.isDeleted ?? false;
        this._redirectRule = definition.rules?.find(r =>
            r.definitionType == DEFINITIONTYPE_REDIRECT &&
            !r.isDeleted);

        // check if value of populated field needs to be updated
        if (this.shouldUpdatePopulatedValue(originalField, temporaryField)) {
            this._values = deepCopy(temporaryField?.value);
            this._tags = deepCopy(originalField.tags)
        }
    }

    public get originalValues() {
        return this._originalField?.values;
    }

    public get reasonForChange() {
        return this._reasonForChange;
    }

    public get hasObjectReasonForChange() {
        return this._reasonForChange?.objectReason != null &&
            this._reasonForChange?.objectReason != undefined &&
            this._reasonForChange?.objectReason != "";
    }

    public setReasonForChange(reasonForChange: AuditReasonOfChangeDto) {
        this._reasonForChange = reasonForChange;
    }


    shouldUpdatePopulatedValue(originalField: MdbsCore.DataFieldDto, temporaryField: MdbsCore.ITemporaryDataFieldDto | undefined) {
        if (!temporaryField || !temporaryField.value)
            return false;

        return temporaryField?.value?.value != originalField?.values?.value ||
            temporaryField?.value?.unit != originalField.values?.unit;
    }

    private _order: number = 0;

    public get order() {
        return this._order;
    }

    public get id() {
        return this._originalField?.id ?? this._temporaryId;
    }

    public get isDeleted() {
        return this._isDeleted;
    }

    private _isVisible: boolean = true;

    public get isVisible() : boolean {
        if (this._isDeleted || this._definition.isDeleted)
            return false;

        if (this._visibilityRules == null)
            this._visibilityRules = this._definition.rules?.filter(r =>
                r.definitionType == DEFINITIONTYPE_VISIBILITY &&
                !r.isDeleted) ?? []

        if (this._visibilityRules.length  == 0) return true;

        const isVisible = this._visibilityRules?.some(r => resolveConstraintExpression(r.constraintExpression, this))
        // if (!isVisible && !!this.values.value && this.values.value !== "")
        // this.resetAlLValues();

        return isVisible;
    }

    public set isVisible(isVisible: boolean) {
        if(this._isVisible != isVisible)
            this._isVisible = isVisible;
    }

    public get redirectRule(): Readonly<RuleDefinitionDto> | undefined {
        return this._redirectRule;
    }

    public get hintRules(): Readonly<RuleDefinitionDto[]> {
        if (this._isDeleted || this._definition.isDeleted)
            return [];

        if (this._hintRules == null)
            this._hintRules = this._definition.rules?.filter(r =>
                r.definitionType == DEFINITIONTYPE_HINT &&
                !r.isDeleted) ?? []

        return this._hintRules ?? [];
    }

    public get isRedirectVisible() {
        if (!this._isVisible || !this._redirectRule || this.redirectRule?.isDeleted)
            return false;

        if (!this._redirectRule.constraintExpression)
            return true;

        return resolveConstraintExpression(this._redirectRule.constraintExpression, this);
    }


    private _isEditable: boolean = true;

    public get isEditable() {
        // editability set from outside through instrument and visit
        if(!this._isEditable || this.isLocked || this._isDeleted || this.definitionType == "DisplayValue")
            return false;

        if (this._isDeleted || this._definition.isDeleted)
            return false;

        // check for editability rule
        if (this._editabilityRules == null)
            this._editabilityRules = this._definition.rules?.filter(r =>
                r.definitionType == DEFINITIONTYPE_EDITABILITY &&
                !r.isDeleted) ?? []

        if (this._editabilityRules.length > 0)
            return this._editabilityRules?.some(r => resolveConstraintExpression(r.constraintExpression, this))

        return true;
    }

    public set isEditable(isEditable: boolean) {
        if (this._isEditable != isEditable) {
            this._isEditable = isEditable;
        }
    }

    public get isRequired() {
        return this.isVisible && (this._definition.rules?.some(r =>
            r.definitionType == DEFINITIONTYPE_REQUIRED_FIELD &&
            !r.isDeleted) ?? false)
    }

    private _isLockedManually: boolean = false;

    public get isLockedManually() {
        return this._isLockedManually;
    }

    private _values: any = {};

    public get values() {
        if (!this._isVisible)
            return {};
        return this._values;
    }

    public get currentValues() {
        return this._values;
    }

    public get hasChanges(): boolean {
        // Compared as JSON: an Autocomplete holds an array, which != compares by reference.
        const dataChanged = JSON.stringify(this._originalField?.values ?? {}) !== JSON.stringify(this._values) &&
            JSON.stringify(this._originalField?.values ?? {value: ""}) !== JSON.stringify(this._values);
        return (dataChanged && this._isVisible) || this.removingChildren.length > 0;
    }

    public get hasVisibilityChanges(): boolean {
        return this._originalField?.isVisible != this._isVisible;
    }

    public get wasVisible(): boolean {
        return this._originalField?.isVisible ?? false;
    }

    public get hasValues() {
        return Object.keys(this._values).length > 0

    }

    private _tags: any = {};

    public get tags() {
        return this._tags;
    }

    private _childFields: DataFieldCache[] = new Array<DataFieldCache>();

    public get childFields() {
        return this._childFields.filter(c => !c.isDeleted && !c.definition.isDeleted);
    }

    public get displayKey() {
        return this._definition.displayKey ?? "";
    }

    private _displayName?: string;

    public get displayName() {
        if (this._displayName === undefined) {
            this._displayName = translate(this.displayKey);
        }
        return this._displayName;
    }

    public get definitionId() {
        return this._definition.id;
    }

    public get definitionType() {
        return this._definition.definitionType ?? "";
    }

    public get definition() {
        return this._definition.definition ?? {};
    }

    public get referenceId() {
        return this._definition.referenceId
    }

    public get referenceType() {
        return this._definition.referenceType
    }

    public get identifier() {
        return this._definition.identifier ?? "";
    }

    public get rules() {
        return this._definition.rules ?? [];
    }

    public get isNewField() {
        return this._originalField === undefined;
    }

    public get isLocked() {
        return this._isLockedManually || (this._originalField?.lockedAutomatically ?? false);
    }

    public get LockDate() {
        return this._originalField?.lockDate
    }

    public get lockedAutomatically() {
        return this._originalField?.lockedAutomatically;
    }

    public get creationDate() {
        return this._originalField?.creationDate;
    }

    public get creatingUser() {
        return this._originalField?.creatingUser;
    }

    public get changeDate() {
        return this._originalField?.changeDate;
    }

    public get changingUser() {
        return this._originalField?.changingUser;
    }

    public deleteValues() {
        this._values = Object.assign({}, {});
    }

    public setValue(key: string, value: any) {
        const newValue: any = {};
        newValue[key] = value;
        this._values = Object.assign({}, this._values, newValue);
    }

    public setValues(values: any) {
        this._values = Object.assign({}, this._values, values);
    }

    public removeValue(key: string) {
        if (key in this._values) {
            const newValues = deepCopy(this._values);
            delete newValues[key];
            this._values = Object.assign({}, newValues);
        }
    }

    public resetValues() {
        this._values = Object.assign({}, deepCopy(this._originalField?.values ?? {}));
    }

    public lockManually() {
        if (!this._isLockedManually) {
            this._isLockedManually = true;
        }
    }

    public unlockManually() {
        if (this._isLockedManually) {
            this._isLockedManually = false;
        }
    }

    public addChild(child: DataFieldCache) {
        if (!this.childFields.find(value => value.id !== undefined && value.id === child.id)) {
            this._childFields.push(child);
            this._childFields.sort((a, b) => (a.order > b.order) ? 1 : -1)
        }
    }

    public addChildren(children: DataFieldCache[]) {
        for (let child of children) {
            this.addChild(child)
        }
    }

    public removingChildren: DataFieldCache[] = [];

    public removeChild(child: DataFieldCache) {
        this.removingChildren.push(child);
        const filteredChildren = this._childFields.filter(value => value != child);
        this._childFields.updateValues(filteredChildren)
    }

    public setTag(key: string, value: string) {
        const newValue: any = {};
        newValue[key] = value;
        this._tags = Object.assign({}, this._tags, newValue);
    }

    public removeTag(key: string) {
        if (key in this._tags) {
            const newValues = deepCopy(this._tags);
            delete newValues[key];
            this._tags = Object.assign({}, newValues);
        }
    }

    public updateOrder(order: number) {
        this._order = order;
    }

}
